import { Button, Checkbox, Flex, Form, TextField } from '@approvalmax/ui/src/components';
import { browserHelpers, hooks } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import trim from 'lodash/trim';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { amplitudeService } from 'services/amplitude';
import { gaService } from 'services/ga';
import { routingService } from 'services/routing';
import { storageService } from 'services/storage';
import { useRegister } from 'shared/data/webApp/v1';
import { getPath, Path, Workspace } from 'urlBuilder';
import { z } from 'zod';

import { ConsentInfo } from '../../../../resources/components';
import { acceptedProductNewsEmailsFieldName, emailFieldName } from './RegisterForm.constants';
import { messages } from './RegisterForm.messages';
import { schema } from './RegisterForm.schema';

export const RegisterForm = memo(() => {
    const [queryEmail] = hooks.useQueryParam('email');
    const [app] = hooks.useQueryParam<Workspace>('app');
    const [token] = hooks.useQueryParam('token');
    const [consentMarketingAccepted] = hooks.useQueryParam('consentMarketingAccepted');

    const { mutate, isLoading } = useRegister({
        onSuccess: async (_, { body }) => {
            const { oneTimeToken, email, acceptedProductNewsEmails: consentMarketingAccepted } = body || {};

            await gaService.sendEvent('sign_up', { method: 'direct' });

            if (oneTimeToken) {
                storageService.setAuthenticated(true);
                routingService.reloadToDefaultPath();
            } else {
                routingService.push(getPath(Path.emailConfirmation, { email, app, consentMarketingAccepted }));
            }
        },
    });

    const form = useForm<z.infer<typeof schema>>({
        defaultValues: {
            [emailFieldName]: queryEmail || '',
            [acceptedProductNewsEmailsFieldName]: Boolean(consentMarketingAccepted),
        },
        resolver: zodResolver(schema),
    });

    const onSubmit = form.handleSubmit((data) => {
        amplitudeService.sendData('signup: click on sign up button', {
            'signup type': 'email',
        });

        mutate({
            body: {
                ...data,
                uiState: encodeURIComponent(JSON.stringify({ app })),
                sendConfirmationLink: true,
                timeZoneForEmail: window.ApprovalMax.userTimeZone,
                oneTimeToken: queryEmail === data[emailFieldName] ? token : undefined,
                isMobileFlow: browserHelpers.isMobile(),
            },
        });
    });

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Flex direction='column' spacing='32' justifyContent='center'>
                <Flex direction='column' maxWidth={400}>
                    <TextField
                        name={emailFieldName}
                        required
                        placeholder={messages.emailPlaceholder}
                        label={messages.email}
                        initFocus
                        clearable={false}
                        inputMode='email'
                        transform={trim}
                    />

                    <Checkbox name={acceptedProductNewsEmailsFieldName} size='small'>
                        {messages.agreement}
                    </Checkbox>
                </Flex>

                <Button type='submit' color='mint80' size='large' block progress={isLoading}>
                    {messages.submitButton}
                </Button>

                <ConsentInfo buttonText={messages.submitButton} />
            </Flex>
        </Form>
    );
});

RegisterForm.displayName = 'RegisterForm';
